import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { ActionLink } from "@/components/shared/action-buttons"
import { useTranslation } from "react-i18next"
import { useIndexProps } from "./page-props"
import { PlusCircle, Inbox, Trash, Pencil } from "lucide-react"
import { PageBody } from "@/components/ui/layout"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Button } from "@/components/ui/button"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { ListingCard } from "@/components/shared/listing-card"
import { Link } from "@/lib/page-rendering"

export default function Page() {
  const { t } = useTranslation(["specialities", "common"])
  const { specialities } = useIndexProps()

  return (
    <>
      <EducatorPageChrome
        action={
          <ActionLink
            variant="solid"
            href="./specialities/new"
            Icon={PlusCircle}
          >
            {t("specialities:index.create")}
          </ActionLink>
        }
      />
      <PageBody role="region">
        <ul className="flex flex-col gap-6">
          {specialities.length === 0 && (
            <Card>
              <CardContent>
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia
                      variant="icon"
                      className="text-muted size-20 bg-white"
                    >
                      <Inbox className="size-16" />
                    </EmptyMedia>
                    <EmptyTitle>
                      {t("specialities:index.empty_list_title")}
                    </EmptyTitle>
                    <EmptyDescription>
                      {t("specialities:index.empty_list_description")}
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Link href="./specialities/new" as={Button}>
                      <PlusCircle />
                      {t("specialities:index.create")}
                    </Link>
                  </EmptyContent>
                </Empty>
              </CardContent>
            </Card>
          )}
          {specialities.map(({ listing_profile, collection }) => (
            <li key={listing_profile.id} className="contents">
              <ListingCard listing={listing_profile}>
                {collection.map((item) => (
                  <li key={item.id}>
                    <Card
                      key={item.id}
                      data-testid="speciality-card"
                      className="h-full"
                    >
                      <CardHeader className="flex flex-row items-center justify-between align-bottom">
                        <CardTitle id={`${item.name}-title`}>
                          {item.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription>{item.description}</CardDescription>
                      </CardContent>
                      <CardFooter className="flex flex-row gap-4">
                        <Link
                          href={`./specialities/${item.id}/edit`}
                          as={Button}
                        >
                          <Pencil />
                          {t("common:actions.edit")}
                        </Link>
                        <ConfirmationDialog
                          action={
                            <Link
                              className={buttonVariants({
                                variant: "destructive",
                              })}
                              href={`./specialities/${item.id}`}
                              method="delete"
                              as={Button}
                            >
                              {t("common:actions.confirm")}
                            </Link>
                          }
                        >
                          <ConfirmationDialogTrigger asChild>
                            <Button variant={"destructive-outline"}>
                              <Trash />
                              {t("common:actions.delete")}
                            </Button>
                          </ConfirmationDialogTrigger>
                        </ConfirmationDialog>
                      </CardFooter>
                    </Card>
                  </li>
                ))}
              </ListingCard>
            </li>
          ))}
        </ul>
      </PageBody>
    </>
  )
}
